/**
 * EasyAccess — front-and-center quick actions for the coach sidebar.
 *
 * Per Sachin (2026-02-13): "I want coaches to easily see who's coming in."
 * This block lives at the TOP of the sidebar with bigger buttons + the
 * day's headcount baked into the "Today's Schedule" tile so they never
 * have to hunt for it.
 *
 * Items (controlled by props so the layout can hide them based on perms):
 *   • Today's Schedule  → /coach/today  (with live headcount pill)
 *   • Quick Check-In    → /coach/check-in
 *   • Inbox             → /coach/messages  (with unread badge)
 *   • Add a Lead        → /coach?quick_add=lead  (opens the QuickAdd modal)
 *
 * Props:
 *   - collapsed (bool): true when the sidebar is in icon-only mode
 *   - canViewSchedule, canViewAttendance, canViewMessages, canAddLead
 *   - unreadMessages: number
 */
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { Zap, Calendar, CheckCircle2, MessageSquare, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared poll — /api/coach/attendance/today returns {rows, counts, ...}
// (the bare /api/coach/today endpoint is a slot-list, wrong shape). We
// exclude `cancelled + cancel_reason==='rescheduled'` so the headcount
// matches the badge total on /coach/today (Scheduled + Came + Assessments).
// Polled every 60s. One hook keeps both the sidebar tile and the floating
// pill in lockstep without doubling API volume.
function useTodayHeadcount(enabled = true) {
    const [n, setN] = useState(null);
    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        const tick = () => {
            axios.get(`${API}/coach/attendance/today`, { withCredentials: true })
                .then((r) => {
                    if (cancelled) return;
                    const rows = r.data?.rows || [];
                    const counted = rows.filter(
                        (row) => !(row.status === "cancelled" && row.cancel_reason === "rescheduled"),
                    );
                    setN(counted.length);
                })
                .catch(() => { /* silent — best-effort */ });
        };
        tick();
        const id = setInterval(tick, 60_000);
        return () => { cancelled = true; clearInterval(id); };
    }, [enabled]);
    return n;
}

export default function EasyAccess({
    collapsed = false,
    canViewSchedule = true,
    canViewAttendance = true,
    canViewMessages = true,
    canAddLead = true,
    unreadMessages = 0,
    onItemClick = null,
}) {
    const todayCount = useTodayHeadcount(canViewAttendance || canViewSchedule);

    const items = [];
    if (canViewSchedule || canViewAttendance) {
        items.push({
            to: "/coach/today",
            icon: Calendar,
            label: "Today's Schedule",
            badge: todayCount,
            testid: "easy-access-today",
            tone: "headline",
        });
    }
    if (canViewAttendance) {
        items.push({
            to: "/coach/check-in",
            icon: CheckCircle2,
            label: "Quick Check-In",
            testid: "easy-access-checkin",
        });
    }
    if (canViewMessages) {
        items.push({
            to: "/coach/messages",
            icon: MessageSquare,
            label: "Messages",
            badge: unreadMessages || null,
            badgeTone: "red",
            testid: "easy-access-inbox",
        });
    }
    if (canAddLead) {
        items.push({
            to: "/coach?quick_add=lead",
            icon: UserPlus,
            label: "Add a Lead",
            testid: "easy-access-add-lead",
        });
    }

    if (items.length === 0) return null;

    return (
        <div data-testid="easy-access" className="px-2 pt-2 pb-3">
            {!collapsed && (
                <div className="px-3 pb-2 text-[9px] tracking-[0.3em] uppercase text-brand-red flex items-center gap-1.5">
                    <Zap className="w-3 h-3" /> Easy Access
                </div>
            )}
            <div className="space-y-1">
                {items.map((it) => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        data-testid={it.testid}
                        title={collapsed ? it.label : undefined}
                        onClick={onItemClick || undefined}
                        className={({ isActive }) => {
                            const base = `relative flex items-center gap-2.5 px-3 py-2 text-sm rounded-md transition-colors border ${
                                collapsed ? "justify-center" : ""
                            }`;
                            const headline = it.tone === "headline";
                            if (isActive) {
                                return `${base} bg-brand-red/15 border-brand-red/50 text-white`;
                            }
                            if (headline) {
                                return `${base} bg-brand-red/10 border-brand-red/40 text-white hover:bg-brand-red/20 hover:border-brand-red/60`;
                            }
                            return `${base} bg-zinc-950 border-zinc-800 text-zinc-200 hover:border-brand-red/50 hover:text-white`;
                        }}
                    >
                        <it.icon className={`w-4 h-4 flex-shrink-0 ${it.tone === "headline" ? "text-brand-red" : ""}`} />
                        {!collapsed && (
                            <>
                                <span className="flex-1 text-left truncate">{it.label}</span>
                                {typeof it.badge === "number" && it.badge > 0 && (
                                    <span
                                        data-testid={`${it.testid}-badge`}
                                        className={`min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full flex items-center justify-center ${
                                            it.badgeTone === "red"
                                                ? "bg-brand-red text-white"
                                                : "bg-white/10 text-white border border-white/20"
                                        }`}
                                    >
                                        {it.badge > 99 ? "99+" : it.badge}
                                    </span>
                                )}
                            </>
                        )}
                        {collapsed && typeof it.badge === "number" && it.badge > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-brand-red text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                {it.badge > 9 ? "9+" : it.badge}
                            </span>
                        )}
                    </NavLink>
                ))}
            </div>
        </div>
    );
}

/**
 * TodayPill — small always-visible floating pill (top-right) that links to
 * /coach/today and shows the live headcount for today. Rendered by
 * CoachLayout once for the whole console so coaches NEVER have to hunt
 * for "who's coming in today".
 */
export function TodayPill() {
    const n = useTodayHeadcount(true);
    return (
        <NavLink
            to="/coach/today"
            data-testid="today-pill"
            className={({ isActive }) =>
                `fixed top-[5.25rem] right-3 md:right-5 z-30 hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs tracking-wider uppercase transition-colors shadow-lg ${
                    isActive
                        ? "bg-brand-red border-brand-red text-white"
                        : "bg-black/90 border-brand-red/60 text-white hover:bg-brand-red hover:border-brand-red backdrop-blur"
                }`
            }
            title="Jump to today's schedule"
        >
            <Calendar className="w-3.5 h-3.5" />
            <span className="font-semibold">Today</span>
            {typeof n === "number" && n > 0 && (
                <span
                    data-testid="today-pill-count"
                    className="min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-white/15 flex items-center justify-center"
                >
                    {n}
                </span>
            )}
        </NavLink>
    );
}
